import React, {PureComponent, Suspense} from 'react'
import PropTypes from 'prop-types'
import {compose} from 'redux'
import {connect} from 'react-redux'
import {createStructuredSelector} from 'reselect'
import {Route, Redirect, Switch} from 'react-router-dom'
import {getRamblerIdHelper} from '@rambler-id/helper'
import {sentryReact as Sentry} from '@rambler-id/sentry'
import {getUrlParams, addUrlParams} from '@rambler-id/url'
import {checkWebAuthnAvailability} from '@rambler-id/webauthn'
import {activateMetrics, trackPageview} from 'utils/metrics'
import {retry} from '@rambler-id/promise'
import {ErrorBoundary} from '@rambler-id/react'
import {provideSnackbar} from 'rambler-ui'
import * as urls from 'utils/urls'
import redirect from 'utils/redirect'
import {loadUser} from 'models/user/actions'
import {selectProfileType} from 'models/user/selectors'
import Snackbar from 'components/Snackbar'
import Progress from './Progress'
import fallbackUrl from './fallbackUrl'
import {
  loadAllowedDomains,
  loadAllowedSchemas,
  loadAbSplits,
  updateUrlQuery,
  checkEmbedding,
  throwError,
  showSnackbar
} from './actions'
import {
  selectSnackbar,
  selectAllowedDomains,
  selectAllowedSchemas
} from './selectors'

import {Topline} from 'components/Topline'
import {Footer} from 'components/Footer'

import styles from './index.css'

export const popupUrls = [
  urls.ADD_PHONE_ACCOUNT,
  urls.ADD_EMAIL_ACCOUNT,
  urls.CHANGE_PASSWORD,
  urls.CHANGE_QUESTION,
  urls.CHANGE_PHONE
]

const RecoveryProfile = React.lazy(
  retry(
    () =>
      import(
        /* webpackChunkName: "recovery-profile" */ 'containers/RecoveryProfile'
      )
  )
)
const ExternalRegistration = React.lazy(
  retry(
    () =>
      import(
        /* webpackChunkName: "external-registration" */ 'containers/ExternalRegistration'
      )
  )
)
const AddEmail = React.lazy(
  retry(() => import(/* webpackChunkName: "add-email" */ 'containers/AddEmail'))
)
const AddPhone = React.lazy(
  retry(
    () =>
      import(
        /* webpackChunkName: "add-phone" */ 'containers/AddPhone/index.tsx'
      )
  )
)
const Profile = React.lazy(
  retry(() => import(/* webpackChunkName: "profile" */ 'containers/Profile'))
)
const Session = React.lazy(
  retry(() => import(/* webpackChunkName: "session" */ 'containers/Session'))
)
const Applications = React.lazy(
  retry(
    () =>
      import(/* webpackChunkName: "applications" */ 'containers/Applications')
  )
)
const DeleteAccount = React.lazy(
  retry(
    () =>
      import(
        /* webpackChunkName: "delete-account" */ 'containers/DeleteAccount'
      )
  )
)
const DeleteProfile = React.lazy(
  retry(
    () =>
      import(
        /* webpackChunkName: "delete-profile" */ 'containers/DeleteProfile'
      )
  )
)
const TwoFactorAuthn = React.lazy(
  retry(
    () =>
      import(
        /* webpackChunkName: "two-factor-authn" */ 'containers/TwoFactorAuthn'
      )
  )
)

const MOBILE_BREAKPOINT = 768

class App extends PureComponent {
  static propTypes = {
    type: PropTypes.string,
    snackbar: PropTypes.object,
    allowedDomains: PropTypes.arrayOf(PropTypes.string),
    allowedSchemas: PropTypes.arrayOf(PropTypes.string),
    loadInitial: PropTypes.func.isRequired,
    reloadUser: PropTypes.func.isRequired,
    throwError: PropTypes.func.isRequired,
    openSnackbar: PropTypes.func.isRequired
  }

  async UNSAFE_componentWillMount() {
    const {loadInitial, reloadUser, updateUrlQuery, checkEmbedding} = this.props

    updateUrlQuery(getUrlParams(window.location.href))
    loadInitial()
    checkEmbedding()
    activateMetrics()

    const ramblerIdHelper = await getRamblerIdHelper()

    ramblerIdHelper.addListener(ramblerIdHelper.Events.LOGIN, reloadUser)
    ramblerIdHelper.addListener(
      ramblerIdHelper.Events.OAUTHLOGIN,
      ({action} = {}) => {
        if (action !== 'attach') {
          reloadUser()
        }
      }
    )
    fallbackUrl(this.props)
  }

  UNSAFE_componentWillReceiveProps({location, snackbar}) {
    const {updateUrlQuery, openSnackbar} = this.props

    if (location !== this.props.location) {
      trackPageview()
      updateUrlQuery(getUrlParams(window.location.href))

      if (
        !~popupUrls.indexOf(location.pathname) &&
        !~popupUrls.indexOf(this.props.location.pathname)
      ) {
        window.scrollTo(0, 0)
      }
    }

    if (snackbar.message && snackbar !== this.props.snackbar) {
      openSnackbar(<Snackbar {...snackbar} />)
    }
  }

  async componentDidMount() {
    checkWebAuthnAvailability()

    const ramblerIdHelper = await getRamblerIdHelper()

    ramblerIdHelper.addListener(ramblerIdHelper.Events.LOGOUT, this.onLogout)

    document.body.setAttribute(
      'data-cerber',
      `id_${window.innerWidth >= MOBILE_BREAKPOINT ? 'desktop' : 'mobile'}`
    )
  }

  async componentWillUnmount() {
    const ramblerIdHelper = await getRamblerIdHelper()

    ramblerIdHelper.removeListener(ramblerIdHelper.Events.LOGOUT, this.onLogout)
  }

  onLogout = () => {
    redirect(urls.LOGIN)
  }

  render() {
    const {type, allowedDomains, allowedSchemas, location} = this.props

    if (
      type === undefined ||
      allowedDomains === undefined ||
      allowedSchemas === undefined
    ) {
      return <Progress />
    }

    return (
      <ErrorBoundary
        captureException={Sentry.captureException}
        fallback={
          <Snackbar
            isOpened
            showClose={false}
            positionY="top"
            type="danger"
            message="Проблема при загрузке, пожалуйста, перезагрузите страницу"
          />
        }>
        <div className={styles.root}>
          <Topline />
          <Suspense
            fallback={
              <div className={styles.fallback}>
                <Progress />
              </div>
            }>
            <Switch>
              <Route
                exact
                path={urls.RECOVERY_PROFILE}
                render={(props) => <RecoveryProfile {...props} />}
              />
              <Route
                exact
                path={urls.REGISTRATION}
                render={({location}) => {
                  const searchParams = Object.fromEntries(
                    new URLSearchParams(location.search)
                  )

                  window.location.href = addUrlParams(
                    `${window.location.origin}${urls.MAIL_REGISTRATION}`,
                    {
                      ...searchParams,
                      rname: 'mail',
                      theme: 'mail'
                    }
                  )

                  return null
                }}
              />
              <Route
                exact
                path={urls.EXT_REGISTRATION}
                render={(props) => <ExternalRegistration {...props} />}
              />
              <Route
                exact
                path={urls.ADD_EMAIL}
                render={(props) => <AddEmail {...props} />}
              />
              <Route
                exact
                path={urls.ADD_PHONE}
                render={(props) => <AddPhone {...props} />}
              />
              <Route
                exact
                path={[
                  urls.PROFILE,
                  urls.DEFAULT_CHAMP,
                  urls.ADD_PHONE_ACCOUNT,
                  urls.ADD_EMAIL_ACCOUNT,
                  urls.CHANGE_PASSWORD,
                  urls.CHANGE_QUESTION,
                  urls.CHANGE_PHONE
                ]}
                render={(props) => <Profile {...props} />}
              />
              <Route
                path={urls.SESSION}
                render={(props) => <Session {...props} />}
              />
              <Route
                exact
                path={urls.APPLICATIONS}
                render={(props) => <Applications {...props} />}
              />
              <Redirect from={urls.DELETE_MAIL} to={urls.PROFILE} />
              <Route
                exact
                path={urls.DELETE_ACCOUNT}
                render={(props) => <DeleteAccount {...props} />}
              />
              <Route
                exact
                path={urls.DELETE_PROFILE}
                render={(props) => <DeleteProfile {...props} />}
              />
              <Route
                exact
                path={urls.TWO_FACTOR_AUTHN}
                render={(props) => <TwoFactorAuthn {...props} />}
              />
              <Redirect
                from="/account"
                to={{
                  pathname: urls.PROFILE,
                  search: location.search
                }}
              />
            </Switch>
          </Suspense>
          <Footer />
        </div>
      </ErrorBoundary>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  type: selectProfileType,
  snackbar: selectSnackbar,
  allowedDomains: selectAllowedDomains,
  allowedSchemas: selectAllowedSchemas
})

function mapDispatchToProps(dispatch) {
  return {
    loadInitial: () => {
      dispatch(loadUser())
      dispatch(loadAllowedDomains())
      dispatch(loadAllowedSchemas())
      dispatch(loadAbSplits())
    },
    reloadUser: () => dispatch(loadUser()),
    throwError: (error) => dispatch(throwError(error)),
    showSnackbar: (message) => dispatch(showSnackbar(message)),
    checkEmbedding: () => dispatch(checkEmbedding()),
    updateUrlQuery: (query) => dispatch(updateUrlQuery(query))
  }
}

const withConnect = connect(mapStateToProps, mapDispatchToProps)

export default compose(provideSnackbar, withConnect)(App)
